import { useState, useEffect, useMemo, useCallback, createContext, useContext, type ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { User as SupaUser } from '@supabase/supabase-js'

interface AppUser {
  id: string
  email: string
  nome: string
  cargo: string
}

export interface Permissoes {
  projetos: 'todos' | 'atribuidos' | 'nenhum'
  financeiro: boolean
  clientes: boolean
  configuracoes: boolean
  propostas: boolean
  gerador_propostas: boolean
}

const DEFAULT_PERMISSOES: Permissoes = {
  projetos: 'nenhum',
  financeiro: false,
  clientes: false,
  configuracoes: false,
  propostas: false,
  gerador_propostas: false,
}

interface AuthContextValue {
  user: AppUser | null
  loading: boolean
  role: string | null
  permissoes: Permissoes
  can: (modulo: keyof Omit<Permissoes, 'projetos'>) => boolean
  projetoScope: 'todos' | 'atribuidos' | 'nenhum'
  /** @deprecated use can('propostas') || can('financeiro') etc */
  canCalendar: boolean
  /** @deprecated use can('configuracoes') */
  canShowroom: boolean
  /** @deprecated use can('propostas') */
  canPropostas: boolean
  signIn: (credential: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]             = useState<AppUser | null>(null)
  const [role, setRole]             = useState<string | null>(null)
  const [permissoes, setPermissoes] = useState<Permissoes>(DEFAULT_PERMISSOES)
  const [loading, setLoading]       = useState(true)

  async function fetchRoleAndPermissions(uid: string | null) {
    if (!uid) { setRole(null); setPermissoes(DEFAULT_PERMISSOES); return }
    try {
      const { data, error } = await supabase
        .from('colaboradores')
        .select('role, papel_id, ativo, escritorio_papeis(nome, permissoes)')
        .eq('auth_user_id', uid)
        .single()
      if (error) { console.error('fetchRole:', error.message); setRole(null); setPermissoes(DEFAULT_PERMISSOES); return }

      // Conta desativada pelo admin: derruba a sessão viva
      if (data && data.ativo === false) {
        setRole(null); setPermissoes(DEFAULT_PERMISSOES)
        await supabase.auth.signOut()
        return
      }

      const papelData = data?.escritorio_papeis as { nome: string; permissoes: Permissoes } | null
      setRole(papelData?.nome ?? data?.role ?? null)

      if (papelData?.permissoes) {
        setPermissoes({ ...DEFAULT_PERMISSOES, ...papelData.permissoes })
      } else {
        // Fallback para role legado
        const isAdmin = data?.role === 'admin' || data?.role === 'financeiro'
        setPermissoes(isAdmin ? {
          projetos: 'todos', financeiro: true, clientes: true,
          configuracoes: true, propostas: true, gerador_propostas: true,
        } : DEFAULT_PERMISSOES)
      }
    } catch {
      setRole(null)
      setPermissoes(DEFAULT_PERMISSOES)
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const u = session ? formatUser(session.user) : null
      setUser(u)
      await fetchRoleAndPermissions(u?.id ?? null)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const u = session ? formatUser(session.user) : null
        setUser(u)
        fetchRoleAndPermissions(u?.id ?? null)
      }
    )
    return () => subscription.unsubscribe()
  }, [])

  const signIn = useCallback(async (credential: string, password: string) => {
    // Aceita "ariane", "@ariane", "Ariane" ou um email; normaliza para o login real.
    const raw = credential.trim().replace(/^@+/, '').toLowerCase()
    const email = raw.includes('@') ? raw : `${raw}@ifp.internal`
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    // Conta desativada: bloqueia com mensagem clara no login
    const uid = data.user?.id
    if (uid) {
      const { data: colab } = await supabase
        .from('colaboradores').select('ativo').eq('auth_user_id', uid).single()
      if (colab && colab.ativo === false) {
        await supabase.auth.signOut()
        throw new Error('Esta conta está desativada. Fale com o administrador.')
      }
    }
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  const can = useCallback((modulo: keyof Omit<Permissoes, 'projetos'>) => {
    return !!permissoes[modulo]
  }, [permissoes])

  const value = useMemo(() => ({
    user, loading, role, permissoes, can,
    projetoScope: permissoes.projetos,
    // Compat: deprecated — usar can() em código novo
    canCalendar:  permissoes.financeiro || permissoes.configuracoes,
    canShowroom:  permissoes.configuracoes,
    canPropostas: permissoes.propostas,
    signIn, signOut,
  }), [user, loading, role, permissoes, can, signIn, signOut])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return ctx
}

function formatUser(raw: SupaUser): AppUser {
  return {
    id:    raw.id,
    email: raw.email ?? '',
    nome:  raw.user_metadata?.nome  ?? raw.email?.split('@')[0] ?? 'Usuário',
    cargo: raw.user_metadata?.cargo ?? 'Arquiteto',
  }
}
